/**
 * @name BackupAssignments
 * @description Componente para visualizar y rechazar las asignaciones de backup en configuración de perfil
 * @author Gerardo de León
 */

import React, { Component } from 'react';
import i18n from 'i18next';
import moment from 'moment';
import styled from 'styled-components';
import { trackPromise } from 'react-promise-tracker';

import { Environment } from '@tycommon/config';
import { DataGrid, LoadingWrapper, FiltersExpansion } from '@tycommon/components';
import { BackupService, ResponseHandlerService, NotificationsService } from '@tycommon/services';
import { Icons, MUI, Flex } from '@tycommon/styles';

// Components
import RejectDialog from './components/reject.dialog';
import BackupLogDialog from './components/backup-log.dialog';

class BackupAssignments extends Component {
  constructor(props) {
    super(props);
    this.userInfo = JSON.parse(localStorage.getItem('ls.infoUsuario')).Empleado;
    this.state = {
      gridConfig: {
        rows: [],
        columns: [
          { id: 'IdBackup', label: i18n.t('IdBackup'), visible: false, filter: null },
          { id: 'IdEmpleado', label: i18n.t('IdEmpleado'), visible: false, filter: null },
          { type: 'complex', id: ['Foto', 'Nombre', 'Puesto'], label: i18n.t('GenericMessages.employee'), visible: true, filter: null, minWidth: 250 },
          { id: 'Responsabilidades', label: i18n.t('GenericMessages.responsibilities'), visible: true, filter: null },
          { id: 'FechaInicio', label: i18n.t('GenericMessages.startDate'), visible: true, filter: 'ShortDateFilter' },
          { id: 'FechaFin', label: i18n.t('GenericMessages.endDate'), visible: true, filter: 'ShortDateFilter' },
          { id: 'Estatus', label: i18n.t('GenericMessages.status'), visible: true, filter: null, sort: true },
          {
            type: 'render', id: 'Comentarios', label: i18n.t('BackupSettings.backupLog'), visible: true, filter: null, sort: false,
            render: (value, row) => <MUI.ButtonLink onClick={() => this.showBackupLogDialog(value, row)}>{i18n.t('GenericMessages.seeDetails')}</MUI.ButtonLink>
          }],
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canSelect: true,
        canCancel: true
      },
      showRejectDialog: {
        data: {},
        open: false
      },
      namesList: [],
      responsabilitiesList: [],
      filters: {
        employee: null,
        responsability: null,
        startDate: moment().month(0).date(1),
        endDate: moment().month(11).date(31),
        status: 0
      },
      showBackupLogDialog: {
        data: {},
        open: false
      },
    };
  }

  componentDidMount() {
    this.updateFilters(this.state.filters);
    this.getResponsabilities();
  }

  getBackupAssigments = (filters) => {
    const { gridConfig } = this.state;

    trackPromise(
      BackupService.getAssigments(filters)
        .then((response) => {
          // Obtiene las listas para los filtros
          let listNames = Array.from(new Set(response.map((request) => request.Nombre))).map((name) => response.find((a) => a.Nombre === name));

          // NOTA: Se resta un día a la Fecha Fin para mostrarla de forma correcta.
          response.map((item) => {
            item.FechaFin = moment(item.FechaFin).subtract(1, 'days');
          });
          gridConfig.rows = response;
          this.setState((prevState) => ({ gridConfig: { ...prevState.gridConfig, gridConfig }, namesList: listNames }));
        })
        .catch((error) => {
          ResponseHandlerService.error(error);
          gridConfig.rows = [];
          this.setState((prevState) => ({ gridConfig: { ...prevState.gridConfig, gridConfig } }));
        }),
      'loading-tab-backup-assignment'
    );
  }

  /* 
    Estatus 1 - Confirmado
    Estatus 2 - Rechazado
    Estatus 3 - Cancelado
    Estatus 1 - Finalizado

    [CANCELAR] No se puede cancelar si esta corriendo el periodio de backup o ya termino.
    [RECHAZAR] No se puede rechazar si el periodo de backup empieza mañana o ya termino.
  */

  rejectBackup = (values, backup, setSubmitting) => {
    //console.log('rjeect', values, backup);
    //console.log('UserInfo', this.userInfo)
    let request = {
      IdBackup: values.id,
      Comentario: values.comment,
      Estatus: 2
    };

    // Notificación - <colaborador> ha rechazado tu solicitud como backup
    let notificationToSend = {
      IdAmbiente: Environment.getCurrent().environmentId,
      URL: '#/configuraperfil',
      IdTipoNotificacion: 130,
      IdEmpleado: backup.IdEmpleado,
      Parametros: JSON.stringify({ Tab: 6 }),
      Propiedades: JSON.stringify({ colaborador: `${this.userInfo.Nombres} ${this.userInfo.Apellidos}` })
    };

    BackupService.put(request)
      .then((response) => {
        setSubmitting(false);
        this.closeDialog('comment');
        this.getBackupAssigments();
        NotificationsService.post(notificationToSend);
        ResponseHandlerService.success(response);
      })
      .catch((error) => {
        setSubmitting(false);
        ResponseHandlerService.error(error);
      });
  }

  showBackupDialog = (item) => {
    //console.log('showBackupDialog', item);
    let data = item ? { ...item[0] } : {};
    this.setState({ showRejectDialog: { data, open: true } });
  };

  closeDialog = (dialog) => {
    if (dialog === 'comment') {
      this.setState({ showRejectDialog: { data: {}, open: false } });
    }
    else if (dialog === 'backupLog') {
      this.setState({ showBackupLogDialog: { data: {}, open: false } });
    }
  }

  // Consulta todas las responsabilidades
  getResponsabilities = () => {
    BackupService.getResponsabilieties()
      .then((response) => {
        this.setState({ responsabilitiesList: response });
      })
      .catch(() => { });
  }


  /** 
* Actualiza los filtros del reporte
* @param filters (employee, status)
*/
  updateFilters = ({ employee, responsability, startDate, endDate, status }) => {
    const filters = {
      employee: employee && employee !== null ? employee.IdEmpleado : null,
      responsability: responsability && responsability !== null ? responsability.IdResponsabilidad : null,
      startDate: startDate ? moment(startDate).format('YYYY/MM/DD') : null,
      endDate: endDate ? moment(endDate).format('YYYY/MM/DD') : null,
      status: status !== 0 ? status : null
    };
    this.getBackupAssigments(filters);
    this.setState({ filters: { employee, responsability, startDate, endDate, status } });
  }

  showBackupLogDialog = (value, row) => {
    let data = {};
    let comments = row;
    this.setState({ showBackupLogDialog: { data, open: true }, comments });
  };

  render() {
    const { gridConfig, namesList, filters, responsabilitiesList, comments } = this.state;

    return (
      <>
        <LoadingWrapper area="loading-tab-backup">

          {/* Filtros */}
          <Flex.Row flex="1">
            <Styled.BoxFilters>
              <FiltersExpansion>
                <MUI.Box padding="8px" width="100%">
                  <MUI.Grid container alignItems="center" spacing={4}>
                    {/* Empleado */}
                    <MUI.Grid item md={2} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.Autocomplete
                          label={i18n.t('GenericMessages.employee')}
                          searchList={namesList}
                          searchBy="Nombre"
                          value={filters.employee}
                          onChange={(newEmployee) => this.updateFilters({
                            employee: newEmployee,
                            responsability: filters.responsability,
                            startDate: filters.startDate,
                            endDate: filters.endDate
                          })}
                        />
                      </Styled.BoxFilter>
                    </MUI.Grid>
                    {/* Responsabilidad */}
                    <MUI.Grid item md={4} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.Autocomplete
                          label={i18n.t('GenericMessages.responsibilities')}
                          searchList={responsabilitiesList}
                          searchBy="Responsabilidad"
                          value={filters.responsability}
                          onChange={(newResponsability) => this.updateFilters({
                            employee: filters.employee,
                            responsability: newResponsability,
                            startDate: filters.startDate,
                            endDate: filters.endDate
                          })}
                        />
                      </Styled.BoxFilter>
                    </MUI.Grid>
                    <MUI.Grid item md={2} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.DatePicker
                          allowKeyboard={false}
                          label={i18n.t('GenericMessages.startDate')}
                          format={i18n.t('DD/MMMM/YYYY')}
                          invalidDateMessage={i18n.t('FormatDates.invalidDate')}
                          value={filters.startDate}
                          onChange={(newDate) => this.updateFilters({
                            employee: filters.employee,
                            responsability: filters.responsability,
                            startDate: newDate,
                            endDate: filters.endDate
                          })}
                          openTo="date"
                        />
                      </Styled.BoxFilter>
                    </MUI.Grid>
                    <MUI.Grid item md={2} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.DatePicker
                          allowKeyboard={false}
                          label={i18n.t('GenericMessages.endDate')}
                          format={i18n.t('DD/MMMM/YYYY')}
                          invalidDateMessage={i18n.t('FormatDates.invalidDate')}
                          value={filters.endDate}
                          onChange={(newDate) => this.updateFilters({
                            employee: filters.employee,
                            responsability: filters.responsability,
                            startDate: filters.startDate,
                            endDate: newDate
                          })}
                          openTo="date"
                        />
                      </Styled.BoxFilter>
                    </MUI.Grid>
                    {/* Estatus */}
                    <MUI.Grid item md={2} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.TextFieldIcon
                          select
                          label={i18n.t('GenericMessages.status')}
                          type="text"
                          name="status"
                          value={filters.status}
                          InputProps={{ startAdornment: (<Icons.FilterList />) }}
                          onChange={(event) => this.updateFilters({
                            employee: filters.backup,
                            responsability: filters.responsability,
                            startDate: filters.startDate,
                            endDate: filters.endDate,
                            status: event.target.value
                          })}
                        >
                          <MUI.SelectOption value={0}>
                            {i18n.t('GenericMessages.all')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={1}>
                            {i18n.t('BackupSettings.confirmed')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={2}>
                            {i18n.t('BackupSettings.rejected')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={3}>
                            {i18n.t('BackupSettings.cancelled')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={4}>
                            {i18n.t('BackupSettings.finalized')}
                          </MUI.SelectOption>
                        </MUI.TextFieldIcon>
                      </Styled.BoxFilter>
                    </MUI.Grid>
                  </MUI.Grid>
                </MUI.Box>
              </FiltersExpansion>
            </Styled.BoxFilters>
          </Flex.Row>

          <ContentWrapper>
            <MUI.Box padding="8px">
              {/* <MUI.ButtonCTA
              type="button"
              color="secondary"
            //disabled={!formikProps.isValid || formikProps.isSubmitting}
            //onClick={formikProps.handleSubmit}
            >
              {i18n.t('DialogConfirm.cancel')}
            </MUI.ButtonCTA> */}

              <DataGrid
                // ref={this.dataGridRef} 
                columns={gridConfig.columns}
                rows={gridConfig.rows}
                canAdd={gridConfig.canAdd}
                canEdit={gridConfig.canEdit}
                canDelete={gridConfig.canDelete}
                canSelect={gridConfig.canSelect}
                canSelectMultiple={false}
                canCancel={gridConfig.canCancel}
                canSort={true}
                isPaginated={true}
                // action={{
                //   component: '',
                //   label: '',
                //   color: '',
                //   disabled: '',
                //   action: ''
                // }}
                onCancel={(item) => this.showBackupDialog(item)}
              />
            </MUI.Box>
          </ContentWrapper>
        </LoadingWrapper>

        {/* Modal para rechazar backups */}
        {this.state.showRejectDialog.open ? (
          <RejectDialog
            data={this.state.showRejectDialog.data}
            open={this.state.showRejectDialog.open}
            onClose={() => this.closeDialog('comment')}
            onRejectBackup={this.rejectBackup}
          />
        ) : null}

        {/* Modal para mostrar la bitácora del backup */}
        {this.state.showBackupLogDialog.open ? (
          <BackupLogDialog
            open={this.state.showBackupLogDialog.open}
            onClose={() => this.closeDialog('backupLog')}
            getBackup={() => this.getBackup()}
            comments={comments}
          />
        ) : null}
      </>
    );
  }
}

const Styled = {};

Styled.BoxFilters = styled.div`
  width: 100%;
  margin-bottom: 8px;
`;

Styled.BoxFilter = styled.div`
  .MuiFormControl-root {
    min-height: 46px;
  }
`;

const ContentWrapper = styled.section`
  display: flex;
  flex-direction: column;
  padding: 8px;
  border: 1px solid ${(props) => props.theme.colors.greyLight};
  border-radius: 6px;
  box-shadow: ${(props) => props.theme.shadows.depth1};
`;

export default BackupAssignments;